(() => {
  const namespaceKey = 'GuruMediaPlayback';
  const markerSelector = 'script[data-guru-media-playback-root]';
  const existingApi = window[namespaceKey];

  if (existingApi?.scan) {
    existingApi.scan(document);
    return;
  }

  const initializedRoots = new WeakSet();
  const cleanupByRoot = new WeakMap();
  const statesByRoot = new WeakMap();
  const stateByVideo = new WeakMap();

  const callSafely = (callback) => {
    try {
      callback();
    } catch (_error) {
      // Detached and browser-owned media objects may throw; the poster remains usable.
    }
  };

  const isPermittedSource = (value) => {
    if (typeof value !== 'string' || value.length === 0 || value.length > 2048 || value !== value.trim()) return false;
    try {
      const url = new URL(value, window.location.href);
      const directShopifyCdn = url.hostname === 'cdn.shopify.com' || url.hostname === 'videos.shopifycdn.com';
      const storeThemeAsset = url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/');
      const localPreviewHost = ['127.0.0.1', 'localhost', '::1'].includes(window.location.hostname);
      const localPreviewAsset = localPreviewHost && storeThemeAsset && url.origin === window.location.origin;
      return (
        !url.username
        && !url.password
        && (
          (url.protocol === 'https:' && (!url.port || url.port === '443') && (directShopifyCdn || storeThemeAsset))
          || (url.protocol === 'http:' && localPreviewAsset)
        )
        && url.pathname.toLowerCase().endsWith('.mp4')
      );
    } catch (_error) {
      return false;
    }
  };

  function initRoot(sectionRoot) {
    if (!sectionRoot || initializedRoots.has(sectionRoot)) return false;

    const videos = [...sectionRoot.querySelectorAll('[data-guru-deferred-video]')];
    if (videos.length === 0) return false;

    const abortController = new AbortController();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const compactViewport = window.matchMedia('(max-width: 767px)');
    const states = new Map();

    const directSource = (video) => {
      const candidate = compactViewport.matches
        ? video.dataset.guruMediaMobileSrc || video.dataset.guruMediaDesktopSrc
        : video.dataset.guruMediaDesktopSrc || video.dataset.guruMediaMobileSrc;
      return isPermittedSource(candidate) ? candidate : '';
    };

    const nestedSources = (video) => [...video.querySelectorAll('source[data-src]')]
      .filter((source) => isPermittedSource(source.dataset.src));

    const hasPermittedSource = (video) => Boolean(directSource(video) || nestedSources(video).length);

    const notifyState = (state) => {
      state.video.dispatchEvent(new CustomEvent('guru:media-statechange'));
    };

    const showPoster = (state) => {
      const wasPlaying = state.shell.classList.contains('is-playing');
      state.shell.classList.remove('is-playing');
      if (wasPlaying) notifyState(state);
    };

    const showVideo = (state) => {
      state.shell.classList.remove('has-media-error');
      state.shell.classList.add('is-playing');
      notifyState(state);
    };

    const pause = (state) => {
      state.playToken += 1;
      state.playPending = false;
      callSafely(() => state.video.pause());
      showPoster(state);
    };

    const detachSource = (state) => {
      pause(state);
      if (!state.sourceAttached && !state.video.hasAttribute('src')) return;
      state.sourceAttached = false;
      state.video.removeAttribute('src');
      nestedSources(state.video).forEach((source) => source.removeAttribute('src'));
      delete state.video.dataset.guruMediaSourceAttached;
      callSafely(() => state.video.load());
    };

    const failPlayback = (state, token) => {
      if (state.destroyed || token !== state.playToken) return;
      state.playbackFailed = true;
      state.shell.classList.add('has-media-error');
      detachSource(state);
    };

    const isUnavailable = (state) => document.hidden || !state.visible || Boolean(state.video.closest('[hidden]'));

    const wantsPlayback = (state) => state.userRequested || (
      state.autoplay
      && !state.userPaused
      && !reducedMotion.matches
    );

    const requestPlayback = (state) => {
      if (state.destroyed || isUnavailable(state) || !state.sourceAttached || state.playbackFailed || !wantsPlayback(state) || state.playPending) return;

      if (!state.video.paused) {
        showVideo(state);
        return;
      }

      const token = ++state.playToken;
      state.playPending = true;
      try {
        Promise.resolve(state.video.play()).then(
          () => {
            if (token !== state.playToken) return;
            state.playPending = false;
            if (state.destroyed || isUnavailable(state) || state.video.paused) {
              callSafely(() => state.video.pause());
              return;
            }
            showVideo(state);
          },
          () => {
            if (token === state.playToken) state.playPending = false;
            failPlayback(state, token);
          },
        );
      } catch (_error) {
        if (token === state.playToken) state.playPending = false;
        failPlayback(state, token);
      }
    };

    const attachSource = (state) => {
      if (state.destroyed || isUnavailable(state) || state.sourceAttached) return false;
      const source = directSource(state.video);
      const sources = nestedSources(state.video);
      if (!source && sources.length === 0) return false;

      if (source) state.video.src = source;
      sources.forEach((nestedSource) => { nestedSource.src = nestedSource.dataset.src; });
      state.video.dataset.guruMediaSourceAttached = 'true';
      state.sourceAttached = true;
      state.video.preload = 'metadata';
      callSafely(() => state.video.load());
      return true;
    };

    const syncState = (state) => {
      if (state.destroyed) return;
      if (state.playbackFailed || !hasPermittedSource(state.video)) {
        detachSource(state);
        return;
      }

      if (isUnavailable(state)) {
        state.userRequested = false;
        pause(state);
        return;
      }

      if (!wantsPlayback(state)) {
        detachSource(state);
        return;
      }

      attachSource(state);
      requestPlayback(state);
    };

    const intersectionObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const state = states.get(entry.target);
            if (!state || state.destroyed) return;
            state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.35;
            syncState(state);
          });
        }, { threshold: [0, 0.35, 0.7] })
      : null;

    videos.forEach((video) => {
      const shell = video.closest('[data-guru-media-shell]');
      if (!shell) return;

      const state = {
        autoplay: video.dataset.guruMediaAutoplay === 'true',
        destroyed: false,
        interactive: video.dataset.guruMediaInteractive === 'true',
        playbackFailed: false,
        playPending: false,
        playToken: 0,
        shell,
        sourceAttached: false,
        userPaused: false,
        userRequested: false,
        video,
        visible: !intersectionObserver,
      };
      state.sync = () => syncState(state);
      states.set(video, state);
      stateByVideo.set(video, state);

      video.addEventListener('error', () => failPlayback(state, state.playToken), { signal: abortController.signal });
      video.addEventListener('play', () => {
        if (state.destroyed || isUnavailable(state)) {
          callSafely(() => video.pause());
          showPoster(state);
          return;
        }
        showVideo(state);
      }, { signal: abortController.signal });
      video.addEventListener('pause', () => {
        if (!state.destroyed) showPoster(state);
      }, { signal: abortController.signal });
      video.addEventListener('ended', () => {
        if (!video.loop) showPoster(state);
      }, { signal: abortController.signal });

      if (intersectionObserver) intersectionObserver.observe(video);
      else syncState(state);
    });

    sectionRoot.addEventListener('guru:media-toggle', (event) => {
      const video = event.detail?.video;
      if (!(video instanceof HTMLVideoElement) || !sectionRoot.contains(video)) return;
      toggle(video);
    }, { signal: abortController.signal });

    if (states.size === 0) {
      abortController.abort();
      intersectionObserver?.disconnect();
      return false;
    }

    const syncAll = () => states.forEach(syncState);
    const swapResponsiveSources = () => {
      states.forEach((state) => {
        state.playbackFailed = false;
        detachSource(state);
        syncState(state);
      });
    };
    reducedMotion.addEventListener('change', syncAll);
    compactViewport.addEventListener('change', swapResponsiveSources);
    document.addEventListener('visibilitychange', syncAll, { signal: abortController.signal });

    const cleanup = () => {
      abortController.abort();
      intersectionObserver?.disconnect();
      reducedMotion.removeEventListener('change', syncAll);
      compactViewport.removeEventListener('change', swapResponsiveSources);
      states.forEach((state) => {
        state.destroyed = true;
        state.playToken += 1;
        detachSource(state);
        stateByVideo.delete(state.video);
      });
      states.clear();
      delete sectionRoot.dataset.guruMediaPlaybackBound;
      initializedRoots.delete(sectionRoot);
      cleanupByRoot.delete(sectionRoot);
      statesByRoot.delete(sectionRoot);
    };

    initializedRoots.add(sectionRoot);
    cleanupByRoot.set(sectionRoot, cleanup);
    statesByRoot.set(sectionRoot, states);
    sectionRoot.dataset.guruMediaPlaybackBound = 'true';
    return true;
  }

  function cleanupRoot(sectionRoot) {
    const cleanup = cleanupByRoot.get(sectionRoot);
    if (!cleanup) return false;
    cleanup();
    return true;
  }

  function initById(sectionRootId) {
    if (!sectionRootId) return false;
    return initRoot(document.getElementById(sectionRootId));
  }

  function scan(scope = document) {
    const markers = [];
    if (scope.matches?.(markerSelector)) markers.push(scope);
    markers.push(...scope.querySelectorAll?.(markerSelector) || []);
    markers.forEach((marker) => initById(marker.dataset.guruMediaPlaybackRoot));
  }

  function syncWithin(scope) {
    if (!scope) return;
    const sectionRoot = scope.closest?.('[data-guru-media-playback-bound]') || scope;
    const states = statesByRoot.get(sectionRoot);
    states?.forEach((state) => state.sync());
  }

  function toggle(video) {
    const state = stateByVideo.get(video);
    if (!state || state.destroyed || !state.interactive) return false;

    if (state.sourceAttached && !state.video.paused) {
      state.userPaused = true;
      state.userRequested = false;
      state.sync();
      return true;
    }

    state.playbackFailed = false;
    state.userPaused = false;
    state.userRequested = true;
    state.visible = true;
    state.sync();
    return true;
  }

  document.addEventListener('shopify:section:load', (event) => {
    initRoot(event.target);
    scan(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => cleanupRoot(event.target));

  const api = { cleanupRoot, initById, initRoot, scan, syncWithin, toggle };
  window[namespaceKey] = api;
  scan(document);

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => record.removedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      if (node.matches('[data-guru-media-playback-bound]')) cleanupRoot(node);
      node.querySelectorAll('[data-guru-media-playback-bound]').forEach(cleanupRoot);
    }));
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) scan(node);
    }));
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
